import { EventSource, metricsBus } from "@/common/buses";
import amplitudeAnalytics, { IProperties } from "@/common/services/amplitude/amplitudeAnalytics";
import seedrandom from "seedrandom";
import {
  loadAmplitudeSessionId,
  setAmplitudeSessionFirstPage,
  setAmplitudeSessionId,
} from "@/common/utils/storage";
import { AmplitudeReturn, Result } from "@amplitude/analytics-types";
import { useAnalyticsStore , SearchInitiator } from "@/common/store/analyticsStore";
import { useGeolocationStore } from "@/common/store/geolocationStore";

const isFullscreen = () => {
  return !!document.querySelector("#root")?.classList?.contains("fullScreen");
};

let isNewSession = false;
let landingPageTracked = false;

const trackPageVisit = async (sendTrack: (isLanding: boolean) => Promise<AmplitudeReturn<Result> | undefined>) => {
  const isLanding = isNewSession && !landingPageTracked;

  await sendTrack(isLanding);

  landingPageTracked = true;
};

const isWatchPage = () => window.location.pathname.startsWith("/watch/");
const isGifTrackable = () => {
  const pathname = window.location.pathname;
  return pathname === "/" || pathname.match(/\/watch\/|\/search\/|\/gifs\/|\/niches\//i);
};

const getEventSource = (): EventSource => {
  const eventSource = useAnalyticsStore.getState().eventSource;
  return eventSource?.name ?? EventSource.Other;
};

const getSearchTag = (): string | undefined => {
  return useAnalyticsStore.getState().searchTag ?? undefined;
};

metricsBus.on("forceFullscreenEntered",() => amplitudeAnalytics.sendForcedFullscreenEntered(getSearchTag()));

metricsBus.on("forceFullscreenExited", () => amplitudeAnalytics.sendForcedFullscreenExitClicked(getSearchTag()));

metricsBus.on("uploadStarted", ({ verified, username }) => {
  if (verified) {
    return amplitudeAnalytics.sendUploadStarted(username);
  }
});

metricsBus.on("uploadTagSelectionStarted", ({ verified, username }) => {
  if (verified) {
    return amplitudeAnalytics.sendUploadTagSelectionStarted(username);
  }
});

metricsBus.on("uploadTagSelectionEnded", ({ count, verified, username }) => {
  if (verified) {
    return amplitudeAnalytics.sendUploadTagSelectionEnded(username, count);
  }
});

metricsBus.on("uploadEnded", ({ verified, username }) => {
  if (verified) {
    return amplitudeAnalytics.sendUploadEnded(username);
  }
});

metricsBus.on("uploadFinished", ({ sourceURL, verified, username }) => {
  if (verified) {
    return amplitudeAnalytics.sendUploadFinished(username, sourceURL);
  }
});

metricsBus.on("uploadSelected", ({ type, verified, username }) => {
  if (verified) {
    return amplitudeAnalytics.sendUploadSelected(username, type);
  }
});

metricsBus.on("uploadSourceDropped", ({ type, verified, username }) => {
  if (verified) {
    return amplitudeAnalytics.sendUploadSourceDropped(username, type);
  }
});

metricsBus.on("uploadSourceConfirmed", ({ type, verified, username }) => {
  if (verified) {
    return amplitudeAnalytics.sendUploadSourceConfirmed(username, type);
  }
});

metricsBus.on("uploadAudienceDefined", ({ verified, username }) => {
  if (verified) {
    return amplitudeAnalytics.sendUploadAudienceDefined(username);
  }
});

metricsBus.on("uploadVideoTrimmed", ({ verified, username }) => {
  if (verified) {
    return amplitudeAnalytics.sendUploadVideoTrimmed(username);
  }
});

metricsBus.on("watchPageVisited", () => {
  return trackPageVisit((isLanding) => {
    return amplitudeAnalytics.sendWatchPageVisited(isLanding, getEventSource(), getSearchTag());
  });
});

metricsBus.on("gifView", ({ gifId }) => {
  if (isGifTrackable()) {
    return amplitudeAnalytics.sendGifViewed(gifId, isFullscreen(), getEventSource(), getSearchTag());
  }
});

metricsBus.on("meaningfulGifView", ({ gifId, percent }) => {
  if (isGifTrackable()) {
    return amplitudeAnalytics.sendGifViewedMeaningful(gifId, isFullscreen(), getEventSource(), percent, getSearchTag());
  }
});

metricsBus.on("gifLiked", ({ gifId, isMainGIF }) => {
  if (isGifTrackable()) {
    return amplitudeAnalytics.sendGifLiked(
      gifId,
      isFullscreen(),
      getEventSource(),
      isWatchPage() ? isMainGIF : undefined,
      getSearchTag(),
    );
  }
});

metricsBus.on("gifUnliked", ({ gifId, isMainGIF }) => {
  if (isGifTrackable()) {
    return amplitudeAnalytics.sendGifUnliked(
      gifId,
      isFullscreen(),
      getEventSource(),
      isWatchPage() ? isMainGIF : undefined,
      getSearchTag(),
    );
  }
});

metricsBus.on("addedToCollection", ({ gifId }) => {
  if (isGifTrackable()) {
    return amplitudeAnalytics.sendGifAddedToCollection(gifId, isFullscreen(), getEventSource(), getSearchTag());
  }
});

metricsBus.on("PWAPageVisited", () => amplitudeAnalytics.sendPWAPageVisited());

metricsBus.on("PWASessionStarted", () => amplitudeAnalytics.sendPWASessionStarted());

metricsBus.on("fullscreenEntered", () => amplitudeAnalytics.sendFullscreenEntered(getSearchTag()));

metricsBus.on("fullscreenExited", () => amplitudeAnalytics.sendFullscreenExited(getSearchTag()));

metricsBus.on("menuButtonClicked", ({ label }) => amplitudeAnalytics.sendNavButtonClicked(label));

metricsBus.on("searchBarOpened", () => amplitudeAnalytics.sendSearchBarClicked());

metricsBus.on("searchQuerySubmitted", ({ query, queryType, queryCount }) => {
  return amplitudeAnalytics.sendSearchQuerySubmitted(query, queryType, queryCount);
});

metricsBus.on("loginCompleted", () => amplitudeAnalytics.sendLoginCompleted());

metricsBus.on("userFetched", ({ user }) => amplitudeAnalytics.identifyUser(user));

metricsBus.on("homePageVisited", () => {
  return trackPageVisit((isLanding) => amplitudeAnalytics.sendHomePageVisited(isLanding));
});

metricsBus.on("managePageVisited", ({ username, verified }) => {
  if (verified) {
    return amplitudeAnalytics.sendManagePageVisited(username);
  }
});

metricsBus.on("tagClicked", ({ tag, section }) => {
  return amplitudeAnalytics.sendTagClicked(tag, getEventSource(), section);
});

metricsBus.on("deviceTracked", async ({ deviceId, deviceType }) => {
  const lastSessionId = loadAmplitudeSessionId();
  const currentSessionId = await amplitudeAnalytics.getSessionId();
  const getDeviceScore = seedrandom(deviceId);

  const properties: IProperties = {
    "Device Score": {
      value: getDeviceScore(),
      type: "set"
    },
    "Device Type": {
      value: deviceType,
      type: "set"
    },
    "Country Code": {
      value: useGeolocationStore.getState().location?.country ?? "",
      type: "set"
    }
  };

  isNewSession = !lastSessionId || lastSessionId !== currentSessionId;

  if (isNewSession) {
    setAmplitudeSessionFirstPage(window.location.pathname);
    setAmplitudeSessionId(currentSessionId);

    properties["First Visited Page"] = {
      value: window.location.pathname,
      type: "set"
    };
  }

  amplitudeAnalytics.setDeviceId(deviceId);
  amplitudeAnalytics.updateUserProperties(properties);
});

metricsBus.on("experimentVariantExposure", ({ experimentKey, experimentVariant }) => {
  return amplitudeAnalytics.updateUserProperties({
    [experimentKey]: {
      value: experimentVariant,
      type: "set"
    }
  });
});

metricsBus.on("firstVisit", ({ timestamp }) => {
  return amplitudeAnalytics.updateUserProperties({
    "First Visit Timestamp": {
      value: timestamp,
      type: "setOnce"
    }
  });
});

metricsBus.on("searchResultClicked", ({ section, title, position, initiator }) => {
  return amplitudeAnalytics.sendSearchResultClicked(section, title, position, initiator ?? SearchInitiator.Other);
});

metricsBus.on("seeAllButtonClicked", ({ section, initiator }) => {
  if (section) {
    return amplitudeAnalytics.sendSeeAllButtonClicked(section, getSearchTag(), initiator?.toString());
  }
});

metricsBus.on("pageClosed", ({
  page,
  type,
  gifsViewed,
  gifsViewedMeaningful,
  timeSpent,
  contentLength,
  searchTag,
}) => {
  amplitudeAnalytics.sendPageClosed(
    page,
    type,
    gifsViewed,
    gifsViewedMeaningful,
    timeSpent,
    contentLength,
    searchTag,
  );
});

metricsBus.on("followUserClicked", ({ userName, source }) => {
  return amplitudeAnalytics.sendFollowedCreator(userName, source, getSearchTag());
});

metricsBus.on("unfollowUserClicked", ({ userName, source }) => {
  return amplitudeAnalytics.sendUnfollowedCreator(userName, source, getSearchTag());
});

metricsBus.on("followUserClickedAnonymously", ({ userName, source }) => {
  return amplitudeAnalytics.sendAnonymouslyClickedFollowCreator(userName, source, getSearchTag());
});

metricsBus.on("quickRecsShown", () => amplitudeAnalytics.sendQuickRecsShown(getSearchTag()));
metricsBus.on("quickRecsClosed", ({ type }) => amplitudeAnalytics.sendQuickRecsClosed(type, getSearchTag()));
metricsBus.on("quickRecsItemClicked", ({ position }) => {
  return amplitudeAnalytics.sendQuickRecsItemClicked(position, getSearchTag());
});

metricsBus.on("nicheGifVoteUpClicked", ({ niche, gif }) => {
  return amplitudeAnalytics.sendNicheGifVoteUpClicked(niche, gif, getSearchTag());
});
metricsBus.on("nicheGifVoteDownClicked", ({ niche, gif }) => {
  return amplitudeAnalytics.sendNicheGifVoteDownClicked(niche, gif, getSearchTag());
});

metricsBus.on("joinNicheClicked", ({ nicheId }) => {
  return amplitudeAnalytics.sendJoinNicheClicked(nicheId, getEventSource(), getSearchTag());
});
metricsBus.on("joinNicheClickedAnonymously", ({ nicheId }) => {
  return amplitudeAnalytics.sendJoinNicheClickedAnonymously(nicheId, getEventSource(), getSearchTag());
});
metricsBus.on("leaveNicheClicked", ({ nicheId }) => {
  return amplitudeAnalytics.sendLeaveNicheClicked(nicheId, getSearchTag());
});
metricsBus.on("nichePreviewClicked", ({ nicheId, source, initiator }) => {
  return amplitudeAnalytics.sendNichePreviewClicked(nicheId, source, initiator);
});

metricsBus.on("profileClicked", ({ creator, source }) => {
  return amplitudeAnalytics.sendProfileClicked(creator, source, getSearchTag());
});

metricsBus.on("loggedOut", () => amplitudeAnalytics.resetIdentity());

metricsBus.on("manageItemInteracted", ({ id, interaction, username, verified }) => {
  if (verified) {
    return amplitudeAnalytics.sendManageItemInteraction(id, username, interaction);
  }
});

metricsBus.on("managePageInteracted", ({ interaction, username, verified }) => {
  if (verified) {
    return amplitudeAnalytics.sendManagePageInteraction(username, interaction);
  }
});

metricsBus.on("nicheGifsOrderChanged", ({ nicheId, order }) => {
  return amplitudeAnalytics.sendNicheGifsOrderChanged(nicheId, order);
});

metricsBus.on("nicheCategoryClicked", ({ category }) => {
  return amplitudeAnalytics.sendNicheCategoryClicked(category);
});

